import { FC, ReactNode } from "react";
import { Autocomplete, Box, Button, MenuItem, Switch, TextField } from "@mui/material";
import { Formik } from "formik";
import ActiveModel from "../../../../models/ActiveModel";
import SubclassActiveCompositeModel from "../../../../models/SubclassActiveComposite.model";
import { ProductViewModel } from "../../../../models/ProductView.model";
import { ItemBox, MainBox } from "../../../main/query/topic/definitions/definitions.styled";
import Periods from "../../../../utils/periods";
import { getPeriodLabel } from "../../../../utils/period-label";
import { AppModal } from "../../../../components/ui";

type Props = {
    open: boolean;
    onClose: () => void;
    btnSaveDisabled: boolean;
    formik: any;
    productViewModel: ProductViewModel;
    setProductViewModel: (model: ProductViewModel) => void;
    productViewInitial: ProductViewModel;
    productSchema: any;
    activeDisabled: boolean;
    subclassDisabled: boolean;
    setSubclassDisabled: (disabled: boolean) => void;
    actives: ActiveModel[];
    subclasses: SubclassActiveCompositeModel[];
};

/** Modal de cadastro/edição de produto (classes/subclasses + dados + restrições). */
const ProductFormModal: FC<Props> = ({
    open,
    onClose,
    btnSaveDisabled,
    formik,
    productViewModel,
    setProductViewModel,
    productViewInitial,
    productSchema,
    activeDisabled,
    subclassDisabled,
    setSubclassDisabled,
    actives,
    subclasses,
}) => {
    const { errors, touched, handleSubmit, getFieldProps } = formik;

    return (
        <AppModal
            open={open}
            onClose={onClose}
            kicker={productViewModel.idProduct ? "Editar" : "Cadastrar"}
            title={productViewModel.idProduct ? "Editar produto" : "Novo produto"}
            maxWidth={900}
            footer={
                <>
                    <Button variant="outlined" color="secondary" onClick={onClose} sx={{ height: 44, borderRadius: "12px", fontWeight: 600 }}>
                        Cancelar
                    </Button>
                    <Button
                        variant="contained"
                        color="primary"
                        disabled={btnSaveDisabled}
                        onClick={() => handleSubmit()}
                        sx={{ height: 44, borderRadius: "12px", fontWeight: 600 }}
                    >
                        Salvar
                    </Button>
                </>
            }
        >
            <Formik initialValues={productViewInitial} validationSchema={productSchema} onSubmit={() => {}}>
                <form noValidate onSubmit={handleSubmit}>
                    <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 3 }}>
                        <Autocomplete
                            multiple
                            value={productViewModel.active}
                            disabled={activeDisabled}
                            id="combo-box-demo"
                            options={actives}
                            sx={{ gridColumn: "span 1" }}
                            isOptionEqualToValue={(option, value) => option.id === value.id}
                            getOptionLabel={(option) => option.name}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    label="Classes"
                                    {...getFieldProps("active")}
                                    error={Boolean(touched.active && errors.active)}
                                    helperText={touched.active && (errors.active as ReactNode)}
                                />
                            )}
                            onChange={(event: any, newValue: any) => {
                                const disabledSubclass = newValue == null || newValue.length == 0;
                                productViewModel.active = newValue;
                                productViewModel.subclass = disabledSubclass ? [] : productViewModel.subclass;
                                setSubclassDisabled(disabledSubclass);
                                setProductViewModel({ ...productViewModel });

                                productViewModel.showConcentration = !newValue?.length
                                    ? true
                                    : newValue.some((active: ActiveModel) => active.showConcentration);

                                if (!productViewModel.showConcentration) {
                                    productViewModel.period = "AMBOS";
                                }

                                setSubclassDisabled(disabledSubclass);
                                setProductViewModel({ ...productViewModel });
                            }}
                        />

                        <Autocomplete
                            multiple
                            value={productViewModel.subclass}
                            disabled={subclassDisabled}
                            id="combo-box-demo"
                            options={subclasses}
                            sx={{ gridColumn: "span 1" }}
                            groupBy={(option) => option.nameActive}
                            getOptionLabel={(option) => option.nameSubclass}
                            renderInput={(params) => <TextField {...params} label="Subclasses" />}
                            onChange={(event: any, newValue: any) => {
                                productViewModel.subclass = newValue;
                                setProductViewModel({ ...productViewModel });
                            }}
                        />

                        <TextField
                            label={"Nome do produto"}
                            {...getFieldProps("productName")}
                            value={productViewModel.productName}
                            variant="outlined"
                            color="primary"
                            fullWidth
                            onChange={(event) => {
                                productViewModel.productName = event.target.value;
                                setProductViewModel({ ...productViewModel });
                            }}
                            error={Boolean(touched.productName && errors.productName)}
                            helperText={touched.productName && errors.productName}
                        />

                        <TextField
                            label={"PH de estabilidade"}
                            {...getFieldProps("phProduct")}
                            value={productViewModel.phProduct}
                            variant="outlined"
                            color="primary"
                            fullWidth
                            onChange={(event) => {
                                productViewModel.phProduct = event.target.value;
                                setProductViewModel({ ...productViewModel });
                            }}
                            error={Boolean(touched.phProduct && errors.phProduct)}
                            helperText={touched.phProduct && errors.phProduct}
                        />

                        {productViewModel.showConcentration && (
                            <>
                                <TextField
                                    label={"Dosagem minima"}
                                    {...getFieldProps("minimumDosage")}
                                    value={productViewModel.minimumDosage}
                                    variant="outlined"
                                    type="number"
                                    color="primary"
                                    fullWidth
                                    inputProps={{ inputMode: "numeric", pattern: "[0-9]*" }}
                                    onChange={(event) => {
                                        productViewModel.minimumDosage = event.target.value;
                                        setProductViewModel({ ...productViewModel });
                                    }}
                                    error={Boolean(touched.minimumDosage && errors.minimumDosage)}
                                    helperText={touched.minimumDosage && errors.minimumDosage}
                                />
                                <TextField
                                    label={"Dosagem máxima"}
                                    {...getFieldProps("maxDosage")}
                                    value={productViewModel.maxDosage}
                                    variant="outlined"
                                    type="number"
                                    color="primary"
                                    fullWidth
                                    inputProps={{ inputMode: "numeric", pattern: "[0-9]*" }}
                                    onChange={(event) => {
                                        productViewModel.maxDosage = event.target.value;
                                        setProductViewModel({ ...productViewModel });
                                    }}
                                    error={Boolean(touched.maxDosage && errors.maxDosage)}
                                    helperText={touched.maxDosage && errors.maxDosage}
                                />
                            </>
                        )}

                        <TextField
                            label={"Incompatibilidade"}
                            {...getFieldProps("incompatibility")}
                            value={productViewModel.incompatibility}
                            variant="outlined"
                            color="primary"
                            fullWidth
                            onChange={(event) => {
                                productViewModel.incompatibility = event.target.value;
                                setProductViewModel({ ...productViewModel });
                            }}
                            error={Boolean(touched.incompatibility && errors.incompatibility)}
                            helperText={touched.incompatibility && errors.incompatibility}
                        />

                        <TextField
                            label={"Fonte"}
                            {...getFieldProps("source")}
                            value={productViewModel.source}
                            variant="outlined"
                            color="primary"
                            fullWidth
                            onChange={(event) => {
                                productViewModel.source = event.target.value;
                                setProductViewModel({ ...productViewModel });
                            }}
                            error={Boolean(touched.source && errors.source)}
                            helperText={touched.source && errors.source}
                        />

                        <TextField
                            label={"Mecanismo de ação"}
                            {...getFieldProps("mechanismAction")}
                            value={productViewModel.mechanismAction}
                            variant="outlined"
                            color="primary"
                            fullWidth
                            multiline
                            rows={4}
                            sx={{ gridColumn: "span 2" }}
                            onChange={(event) => {
                                productViewModel.mechanismAction = event.target.value;
                                setProductViewModel({ ...productViewModel });
                            }}
                            error={Boolean(touched.mechanismAction && errors.mechanismAction)}
                            helperText={touched.mechanismAction && errors.mechanismAction}
                        />

                        <TextField
                            label={"Descrição"}
                            {...getFieldProps("description")}
                            value={productViewModel.description}
                            variant="outlined"
                            color="primary"
                            fullWidth
                            multiline
                            rows={4}
                            sx={{ gridColumn: "span 2" }}
                            onChange={(event) => {
                                productViewModel.description = event.target.value;
                                setProductViewModel({ ...productViewModel });
                            }}
                            error={Boolean(touched.description && errors.description)}
                            helperText={touched.description && errors.description}
                        />

                        <TextField
                            select
                            fullWidth
                            {...getFieldProps("period")}
                            name={"period"}
                            label="Período"
                            value={productViewModel.period}
                            sx={{ gridColumn: "span 2" }}
                            onChange={(event) => {
                                productViewModel.period = event.target.value;
                                setProductViewModel({ ...productViewModel });
                            }}
                            disabled={!productViewModel.showConcentration}
                            error={Boolean(touched.period && errors.period)}
                            helperText={touched.period && errors.period}
                        >
                            {Periods.map((option) => (
                                <MenuItem key={option} value={option}>
                                    {getPeriodLabel(option)}
                                </MenuItem>
                            ))}
                        </TextField>

                        <Box sx={{ gridColumn: "span 2", display: "flex", gap: 3, flexWrap: "wrap" }}>
                            <MainBox>
                                <ItemBox>
                                    <p>{"PERMITIDO EM GESTANTES"}</p>
                                    <Switch
                                        checked={productViewModel.pregnant}
                                        onChange={(event) => {
                                            productViewModel.pregnant = event.target.checked;
                                            setProductViewModel({ ...productViewModel });
                                        }}
                                        name={"pregnant"}
                                    />
                                </ItemBox>
                            </MainBox>

                            <MainBox>
                                <ItemBox>
                                    <p>{"VEGANO"}</p>
                                    <Switch
                                        checked={productViewModel.vegan}
                                        onChange={(event) => {
                                            productViewModel.vegan = event.target.checked;
                                            setProductViewModel({ ...productViewModel });
                                        }}
                                        name={"vegan"}
                                    />
                                </ItemBox>
                            </MainBox>

                            <MainBox>
                                <ItemBox>
                                    <p>{"ORGÂNICO"}</p>
                                    <Switch
                                        checked={productViewModel.organic}
                                        onChange={(event) => {
                                            productViewModel.organic = event.target.checked;
                                            setProductViewModel({ ...productViewModel });
                                        }}
                                        name={"organic"}
                                    />
                                </ItemBox>
                            </MainBox>
                        </Box>
                    </Box>
                </form>
            </Formik>
        </AppModal>
    );
};

export default ProductFormModal;
